import styled from "@emotion/styled";
import { GrProgressCircle, GrStack, palette } from "@jnpmedi/grebob";
import { DOCUMENT_SIGN_PDF_PAGE_CACHE_DELIMITER, DOCUMENT_SIGN_PDF_PAGE_CACHE_PREFIX } from "@newComponents/myDocument/SignerTokenView";
import { MarkerZoneType, useDocumentContext } from "@newHooks/context/document/document.context";
import { useDocumentSignDocsPdfPageQuery } from "@newHooks/query/documentSign/documentSignDocsPdfPage.query";
import { useDocumentSignerDocsPdfPageQuery } from "@newHooks/query/documentSigner/documentSignerDocsPdfPage.query";
import { useFolderFileDocsPdfPageQuery } from "@newHooks/query/folderFile/folderFileDocsPdfPage.query";
import { useFolderFileDocsPdfPageBySharingLinkQuery } from "@newHooks/query/folderSharingLink/folderFileDocsPdfPageBySharingLink.query";
import { EConsentDocsPdfViewerPageMarkerZone } from "./EConsentDocsPdfViewerPageMarkerZone";
import { FileDetailViewerPageMarkerZone } from "./fileDetailViewerPageMarkerZone/FileDetailViewerPageMarkerZone";
import { DocumentSignerSignatureExt } from "./types";
import { testKey } from "@maven-rest/automationTest/e2e/docs/testKey";
import { DocumentSignerAttrType, DocumentSignResource, GetDocumentSignDocsPdfPageResponse } from "@maven-rest/docs/DocumentSign.dto";
import { DocumentAccessRequesterType } from "@maven-rest/docs/DocumentSigner.dto";
import { GetFolderFileDocsPdfPageResponse } from "@maven-rest/docs/FolderFile.dto";
import { GetFolderFileDocsPdfPageBySharingLinkResponse } from "@maven-rest/docs/FolderSharingLink.dto";
import dayjs from "dayjs";

const PAGE_INNER_SIZE = "30px";
const DEFAULT_PDF_PAGE_REQUEST_RETRY_DELAY = 7500; // In millisec
const DEFAULT_PDF_PAGE_REQUEST_RETRY_COUNT = 100;

export function FileDetailViewerPdfSection({
  fileId,
  docsPdfId,
  page,
  zoom,
  width,
  height,
  documentSign,
  token,
  accessToken,
  onMarkerClicked,
  signatures,
  latestDocumentId,
  documentAccessRequesterType,
}: {
  fileId: bigint;
  docsPdfId: bigint;
  page: number;
  zoom: number;
  width: number;
  height: number;
  documentSign?: DocumentSignResource;
  token?: string;
  accessToken?: string;
  onMarkerClicked?: (signerUuid: string, attrUuid: string, attr: DocumentSignerAttrType) => void;
  signatures?: DocumentSignerSignatureExt[];
  latestDocumentId?: bigint;
  documentAccessRequesterType: DocumentAccessRequesterType;
}) {
  const { markerZoneType } = useDocumentContext();

  const cacheKey = [DOCUMENT_SIGN_PDF_PAGE_CACHE_PREFIX, `${docsPdfId}`, `${page}`, dayjs().format("YYYYMMDD")].join(
    DOCUMENT_SIGN_PDF_PAGE_CACHE_DELIMITER
  );
  const cachedSvgUrl = sessionStorage.getItem(cacheKey);

  const isSharingFileDocsPdfRequest = !!accessToken;
  const isDocumentSignDocsPdfRequest = !token && !!documentSign;
  const isFolderFileDocsPdfRequest = !accessToken && !token && !documentSign;
  const isDocumentSignerAndReferrerDocsPdfRequest = !!token && !documentSign;

  const handleDocsPdfPageSuccess = (
    data: GetFolderFileDocsPdfPageBySharingLinkResponse | GetDocumentSignDocsPdfPageResponse | GetFolderFileDocsPdfPageResponse
  ) => {
    const url = data.docsPdfPage.svgUrl;

    if (url) {
      sessionStorage.setItem(cacheKey, url);
    }
  };

  // NOTE: accessToken 있을 때만 사용
  const { data: sharingFileDocsPdfPageData, isInitialLoading: isSharingFileDocsPdfPageLoading } =
    useFolderFileDocsPdfPageBySharingLinkQuery(
      { fileId, page, docsPdfId, accessToken: accessToken || "" },
      {
        enabled: !cachedSvgUrl && isSharingFileDocsPdfRequest,
        retry: checkDocsPdfRetryExceed,
        retryDelay: DEFAULT_PDF_PAGE_REQUEST_RETRY_DELAY,
        onSuccess: handleDocsPdfPageSuccess,
      }
    );

  // NOTE:
  const { data: documentSignPdfPageData, isInitialLoading: isDocumentSignPdfPageLoading } = useDocumentSignDocsPdfPageQuery(
    { documentSignId: documentSign?.id || BigInt(-1), page, docsPdfId },
    {
      enabled: !cachedSvgUrl && isDocumentSignDocsPdfRequest,
      retry: checkDocsPdfRetryExceed,
      retryDelay: DEFAULT_PDF_PAGE_REQUEST_RETRY_DELAY,
      onSuccess: handleDocsPdfPageSuccess,
    }
  );

  // NOTE: documentSign 없을 때만 사용
  const { data: folderFileDocsPdfData, isInitialLoading: isFolderFileDocsPdfPageLoading } = useFolderFileDocsPdfPageQuery(
    { fileId, page, docsPdfId },
    {
      enabled: !cachedSvgUrl && isFolderFileDocsPdfRequest,
      retry: checkDocsPdfRetryExceed,
      retryDelay: DEFAULT_PDF_PAGE_REQUEST_RETRY_DELAY,
      onSuccess: handleDocsPdfPageSuccess,
    }
  );

  // NOTE: 서명자 및 참조자 전용
  const { data: documentSignerAndReferrerDocsPdfData, isInitialLoading: isDocumentSignerAndReferrerDocsPdfLoading } =
    useDocumentSignerDocsPdfPageQuery(
      { token: token || "", page, docsPdfId, documentAccessRequesterType, documentId: latestDocumentId },
      {
        enabled: !cachedSvgUrl && isDocumentSignerAndReferrerDocsPdfRequest,
        retry: checkDocsPdfRetryExceed,
        retryDelay: DEFAULT_PDF_PAGE_REQUEST_RETRY_DELAY,
        onSuccess: handleDocsPdfPageSuccess,
      }
    );

  const isPdfRendering =
    isSharingFileDocsPdfPageLoading ||
    isDocumentSignPdfPageLoading ||
    isDocumentSignerAndReferrerDocsPdfLoading ||
    isFolderFileDocsPdfPageLoading;

  const resolveDocsPdfPageSvgUrl = () => {
    if (isPdfRendering) return "";

    if (cachedSvgUrl) return cachedSvgUrl;
    if (isSharingFileDocsPdfRequest) return sharingFileDocsPdfPageData?.docsPdfPage.svgUrl;
    if (isDocumentSignDocsPdfRequest) return documentSignPdfPageData?.docsPdfPage.svgUrl;
    if (isFolderFileDocsPdfRequest) return folderFileDocsPdfData?.docsPdfPage.svgUrl;
    if (isDocumentSignerAndReferrerDocsPdfRequest) return documentSignerAndReferrerDocsPdfData?.docsPdfPage.svgUrl;
  };

  const renderMarkerZone = () => {
    if (markerZoneType === MarkerZoneType.EConsent) {
      return (
        <EConsentDocsPdfViewerPageMarkerZone
          key={`pdf-viewer-econsent-marker-zone-${cacheKey}`}
          page={page}
          zoom={zoom}
          width={width}
          height={height}
          onMarkerClicked={onMarkerClicked}
          signatures={signatures}
          signType={documentSign?.type}
        />
      );
    }

    return (
      <FileDetailViewerPageMarkerZone
        key={`pdf-viewer-esign-marker-zone-${cacheKey}`}
        page={page}
        zoom={zoom}
        width={width}
        height={height}
        onMarkerClicked={onMarkerClicked}
        signatures={signatures}
        signType={documentSign?.type}
      />
    );
  };

  return (
    <PdfViewerStackWrapper>
      {!cachedSvgUrl && isPdfRendering ? (
        <PdfViewerLoadingStackWrapper>
          <GrProgressCircle variant="indeterminate" size={30} />
        </PdfViewerLoadingStackWrapper>
      ) : (
        <GrStack className={testKey.common.docsPdfViewerPage(page)}>
          <PdfImageWrapper src={resolveDocsPdfPageSvgUrl()} alt={`Page: ${page}`} />

          {renderMarkerZone()}
        </GrStack>
      )}
    </PdfViewerStackWrapper>
  );
}

function checkDocsPdfRetryExceed(failureCount: number): boolean {
  return failureCount < DEFAULT_PDF_PAGE_REQUEST_RETRY_COUNT;
}

const PdfViewerStackWrapper = styled(GrStack)`
  height: 100%;
  width: 100%;
  background: ${palette.white};
  box-shadow: 0 0 4px #ddd;
  position: relative;
`;

const PdfViewerLoadingStackWrapper = styled(GrStack)`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  margin-top: calc(${PAGE_INNER_SIZE} / -2);
  margin-left: calc(${PAGE_INNER_SIZE} / -2);
  display: flex;
`;

const PdfImageWrapper = styled.img`
  width: 100%;
  height: 100%;
`;
